import React, {Component, ReactElement, ReactNode} from 'react'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Messages} from '../../../Messages'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {formatDateTimeWithoutSeconds} from '../../../formatters/formatDate'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import NatLoginBeforeMessageRepo from './repository/NatLoginBeforeMessageRepo'
import {LoginBeforeMessageMasterInfo, NatGetLoginBeforeMessage} from './repository/NatGetLoginBeforeMessage'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {NatDeleteLoginBeforeMessage} from './repository/NatDeleteLoginBeforeMessage'
import styled from 'styled-components'
import {Location} from 'react-router-dom'

const SELECTED_ADMINISTORATOR_MODE: string = 'selectedMode'

interface LocationState {
    initialMessage: HeadingAreaMessageParams
}

interface Props {
    loginBeforeMessageMasterId: string
    loginBeforeMessageRepo: NatLoginBeforeMessageRepo
    eventEmitter: EventEmitterExAdmin
    location: Location<LocationState>
}

interface States {
    loginBeforeMessage: LoginBeforeMessageMasterInfo
    isLoadCompleted: boolean
}


class NatAdminLoginBeforeMessagePage extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = SessionStorageHelper.getStringItemOrNull(SELECTED_ADMINISTORATOR_MODE)

        this.state = {
            loginBeforeMessage: {
                loginBeforeMessageMasterId: '',
                title: '',
                message: '',
                publishStartDatetime: '',
                publishEndDatetime: '',
                version: 0,
            },
            isLoadCompleted: false,
        }
        this.backLoginBeforeMessageListLinkClick = this.backLoginBeforeMessageListLinkClick.bind(this)
        this.editLoginBeforeMessageLinkClick = this.editLoginBeforeMessageLinkClick.bind(this)
        this.deleteLoginBeforeMessage = this.deleteLoginBeforeMessage.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            // ログイン前メッセージマスタ取得
            const loginBeforeMessage: Nullable<NatGetLoginBeforeMessage> = await this.props.asyncCallFunction(
                this.props.loginBeforeMessageRepo.getLoginBeforeMessageMaster.bind(
                    this.props.loginBeforeMessageRepo, this.props.loginBeforeMessageMasterId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (loginBeforeMessage && loginBeforeMessage.details) {
                this.setState({loginBeforeMessage: loginBeforeMessage.details})
            }
            this.showInitialMessage()
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    backLoginBeforeMessageListLinkClick(): void {
        this.props.navigate(`/admin/loginBeforeMessageList`)
    }

    editLoginBeforeMessageLinkClick(): void {
        this.props.navigate(`/admin/loginBeforeMessageEdit/${this.props.loginBeforeMessageMasterId}`)
    }

    // 削除ボタン押下
    async deleteLoginBeforeMessage(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0011.errorLevel,
                message: Messages.IOPCC0011.toString(this.state.loginBeforeMessage.title),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleDeleteOkClick,
                    },
                ],
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleDeleteOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatDeleteLoginBeforeMessage> = await this.props.asyncCallFunction(
                this.props.loginBeforeMessageRepo.deleteLoginBeforeMessage.bind(
                    this.props.loginBeforeMessageRepo, this.props.loginBeforeMessageMasterId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response && response.requestError === null) {
                this.props.navigate(`/admin/loginBeforeMessageList`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0006.toString('ログイン前メッセージ', this.state.loginBeforeMessage.title),
                            },
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }


    renderDetailsTable(): ReactNode {
        const publishStartDatetime = formatDateTimeWithoutSeconds(this.state.loginBeforeMessage.publishStartDatetime)
        const publishEndDatetime = formatDateTimeWithoutSeconds(this.state.loginBeforeMessage.publishEndDatetime)
        return (
            <>
                <Table>
                    <StyledTableCaption>ログイン前メッセージ情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>タイトル</TableRowName>
                            <TableCell>{this.state.loginBeforeMessage.title}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>メッセージ</TableRowName>
                            <TableCellWordBreakAll>
                                <div dangerouslySetInnerHTML={{__html: this.state.loginBeforeMessage.message}}/>
                            </TableCellWordBreakAll>
                        </TableRow>
                        <TableRow>
                            <TableRowName>掲載期間</TableRowName>
                            <TableCell>{publishStartDatetime} から {publishEndDatetime} まで</TableCell>
                        </TableRow>
                    </TableBody>

                </Table>
            </>
        )
    }

    renderPageHeaderArea(): Nullable<ReactNode> {
        return (
            <>
                <PageFotterAreaRight>
                    <DeleteLink
                        onClick={this.deleteLoginBeforeMessage}>
                        ログイン前メッセージを削除する
                    </DeleteLink>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <AdminPrimaryButton
                        onClick={this.editLoginBeforeMessageLinkClick}>
                        編集する
                    </AdminPrimaryButton>
                </PageFotterAreaLeft>
            </>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/loginBeforeMessageList`}
                    >
                        ログイン前メッセージ一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_LOGIN_BEFORE_MESSAGE}>
                    <Box mb={'15px'}>
                        <PageFooterArea>
                            {this.renderPageHeaderArea()}
                        </PageFooterArea>
                    </Box>
                </AuthorityChecker>
                <PageMainArea>
                    {this.renderDetailsTable()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminLoginBeforeMessagePage)

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`
